// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import { GetConfigResponse } from './generated';
import { isNullOrEmptyString } from './utilities';
import { ApiEnvironment } from './vsCodeEduClientExt';

export type ConfigSettingsAndFeatureFlags = {
    settings: ConfigSettings;
    featureFlags: ConfigFeatureFlags;
};

export type ConfigSettings = {
    [key: string]: string;
};

export type ConfigFeatureFlags = {
    [key: string]: boolean;
};

/**
 * Parses the feature flags and settings from the config api response
 */
export function parseSettingAndFeatureFlags(
    env: ApiEnvironment,
    config: GetConfigResponse
): ConfigSettingsAndFeatureFlags {
    const featureFlags: ConfigFeatureFlags = {};
    const settings: ConfigSettings = {};

    const featureFlagPrefix = getFeatureFlagPrefix(env);
    const settingPrefix = getSettingPrefix(env);

    for (const [key, value] of Object.entries(config)) {
        // Filter out the feature flags by checking if
        // the key starts with the feature flag prefix
        if (key.startsWith(featureFlagPrefix)) {
            const flagName = key.substring(featureFlagPrefix.length);

            // eslint-disable-next-line security/detect-object-injection
            featureFlags[flagName] = value === 'true';
        } else if (key.startsWith(settingPrefix)) {
            const settingName = key.substring(settingPrefix.length);

            if (!isNullOrEmptyString(value)) {
                // eslint-disable-next-line security/detect-object-injection
                settings[settingName] = value;
            }
        }
    }
    return {
        settings,
        featureFlags,
    };
}

/**
 * Creates the config object for the config api request
 * containing the settings and feature flags
 */
export function createSettingsAndFeatureFlags(
    env: ApiEnvironment,
    config: ConfigSettingsAndFeatureFlags
): GetConfigResponse {
    const processedConfig: GetConfigResponse = {};

    for (const [key, value] of Object.entries(config.featureFlags)) {
        processedConfig[createFeatureFlagKey(env, key)] = value.toString();
    }

    for (const [key, value] of Object.entries(config.settings)) {
        processedConfig[createSettingKey(env, key)] = value;
    }

    return processedConfig;
}

//#region Helper functions
/**
 * creates the setting key for the config api request
 * It is of the form {env}.settings.{flagName}
 */
function createSettingKey(env: ApiEnvironment, flagName: string): string {
    return `${getSettingPrefix(env)}${flagName}`;
}

function getSettingPrefix(env: ApiEnvironment): string {
    return `${env}.settings.`;
}

/**
 * creates the feature flag key for the config api request
 * It is of the form {env}.featureFlags.{flagName}
 */
function createFeatureFlagKey(env: ApiEnvironment, flagName: string): string {
    return `${getFeatureFlagPrefix(env)}${flagName}`;
}

function getFeatureFlagPrefix(env: ApiEnvironment): string {
    return `${env}.featureFlags.`;
}
//#endregion
